import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { orderDetailSegment } from '../utils/asoebiOrdersRoute';
import '../styles/components/GuestAsoebiTracker.css';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from './NotificationProvider';
import { useConfirm } from './ui/ConfirmProvider';
import EmptyState from './EmptyState';
import Icon from './Icon';
import AttachmentsPanel from './ui/AttachmentsPanel';
import SearchableSelect from './ui/SearchableSelect';
import RichTextEditor from './ui/RichTextEditor';
import { CurrencyInput } from './ui/CurrencyInput';
import LinkedRecordRow from './ui/LinkedRecordRow';
import { FabricAssignment, GuestAsoebiProfile, Fabric } from '../types/ui/Asoebi';
import { createFabricAssignment as createAssignment, updateFabricAssignment as updateAssignment, recordPayment, archiveFabricAssignment } from '../services/AsoebiService';
import { loadGuests, refreshGuestsFromApi } from '../services/GuestService';
import { loadBundles, refreshBundlesFromApi } from '../services/BundleService';
import { UnifiedBundle } from '../types/Product';
import { Guest } from '../types/Guests';
import { useSortableTable } from '../hooks/useSortableTable';
import { useMoney } from '../hooks/useMoney';
import { sameId, parseId, fmtId } from '../utils/jiraId';
import { recordLabel } from '../utils/recordId';
import { RecordLabelText } from './ui/idColumn';
import { sideLabel } from '../utils/guestSide';
import { entityPath, ENTITY_PREFIX } from '../utils/entityRoutes';
import { listOrderPayments } from '../services/AsoebiService';
type AssignmentPayment = { id: string; amount: string; currency: string | null; date: string | null; method: string | null; status: string | null; reference: string | null; paid_by: string | null; notes: string | null };
const loadAssignmentPayments = (id: string) => listOrderPayments(id).then((r) => r.payments as AssignmentPayment[]);
// Packing assignments (AsoebiAssignmentService) have no backend yet on port/kal; this list stays empty until that API lands.
type AsoebiAssignment = { id: string };
const loadAsoebiAssignments = async (): Promise<AsoebiAssignment[]> => [];
import RecordActivity from './ui/RecordActivity';
import LineItemsEditor, { LineItemFieldConfig } from './ui/LineItemsEditor';
import { MaskedValue } from './ui/MaskedValue';
// .poe-collapsible-row / .poe-collapsible-heading / .poe-section-count — the
// shared collapsible sub-section header the Delivery and Payment sections on
// this same page already use.
import '../styles/components/ProcurementOrderEdit.css';
import { FabricRef, isBundleRef } from '../utils/fabricRef';

// Same words the status bar above the form uses, so a logged transition
// reads in the vocabulary the user just clicked.
const ORDER_STATUS_LABELS: Record<string, string> = {
  pending: 'Awaiting',
  pending_confirmation: 'Awaiting confirmation',
  ordered: 'Ordered',
  paid: 'Paid',
  delivered: 'Delivered',
};

/** Columns for a package's contents. Read-only — see the Package Items
 *  section in the form body for why there is no per-order override. */
const PACKAGE_ITEM_FIELDS: LineItemFieldConfig[] = [
  { key: 'productName', type: 'text', header: 'Item', placeholder: 'Item', row: 1 },
  { key: 'quantity', type: 'number', header: 'Qty', placeholder: 'Qty', min: '0', row: 1 },
  { key: 'unitLabel', type: 'text', header: 'Unit', placeholder: 'Unit', row: 1, width: 90 },
];

/**
 * Columns for the payments already recorded against this order. 'readonly'
 * rather than disabled inputs: these are real PY-### records with their own
 * edit page (the row's arrow opens it), so an input-shaped field here would
 * imply a save path that doesn't exist. The reference column grows — it's the
 * only free-text field and the one most likely to be long.
 */
const PAYMENT_ITEM_FIELDS: LineItemFieldConfig[] = [
  { key: 'payment', type: 'readonly', header: 'Payment', row: 1, width: 90 },
  { key: 'date', type: 'readonly', header: 'Date', row: 1, width: 110 },
  { key: 'method', type: 'readonly', header: 'Method', row: 1, width: 120 },
  {
    key: 'reference',
    type: 'readonly',
    header: 'Reference',
    row: 1,
    renderValue: value => (value ? <MaskedValue value={value} /> : '—'),
  },
  { key: 'amount', type: 'currency', header: 'Amount', row: 1 },
];

interface GuestAsoebiTrackerProps {
  assignments: FabricAssignment[];
  guestProfiles: GuestAsoebiProfile[];
  fabrics: Fabric[];
  onPayment: (assignment: FabricAssignment) => void;
  onUpdate: () => void;
  triggerAssignForm?: boolean;
  onAssignFormOpened?: () => void;
  statusFilter?: string;
}

const GuestAsoebiTracker: React.FC<GuestAsoebiTrackerProps> = ({
  assignments,
  guestProfiles,
  fabrics,
  onPayment,
  onUpdate,
  triggerAssignForm,
  onAssignFormOpened,
  statusFilter = 'all',
}) => {
  const { user: authUser } = useAuth();
  const { showSuccess, showError } = useNotifications();
  const { format: formatMoney, symbol: currencySymbolDisplay, unit: currencyUnit } = useMoney();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const location = useLocation();
  // This component is the ORDER experience only (asoebi_orders, AOS-xxx).
  // It used to answer to /asoebi/assignments/:id as well, back when a single
  // asoebi_orders row was given two URLs and two prefixes for the same
  // number. Assignments are real asoebi_assignments rows now, with their own
  // ids and their own component (AsoebiAssignmentsView), so every route here
  // is an order route and Cancel/Save/Reject/Delete return to the Orders list.
  const returnTarget = '/asoebi/orders';
  // A guest can have more than one order (a reorder, a second package), so
  // the list view's own URL can't stand in for a specific record — this
  // mirrors AsoebiDeliveryDashboard's /asoebi/deliveries/:id pattern so a
  // given order is linkable, bookmarkable, and reachable by Back.
  const trackerRoute = (() => {
    const segment = orderDetailSegment(location.pathname);
    if (segment !== null) {
      return { open: true, editId: segment === 'new' ? null : parseId(ENTITY_PREFIX.fabricAssignment, segment) };
    }
    return { open: false, editId: null as string | null };
  })();
  const [guests, setGuests] = useState<Guest[]>(() => loadGuests());
  const [bundles, setBundles] = useState<UnifiedBundle[]>(() => loadBundles());
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignType, setAssignType] = useState<'fabric' | 'bundle'>('fabric');
  const [selectedGuest, setSelectedGuest] = useState<Guest | null>(null);
  const [newAssignment, setNewAssignment] = useState({
    fabric_id: '',
    yards: 1,
    price_per_yard: 0,
    notes: '',
    phone: '',
    email: '',
  });
  const [payNow, setPayNow] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: 0, method: 'transfer', reference: '' });
  const [editingAssignment, setEditingAssignment] = useState<FabricAssignment | null>(null);
  // Recording a payment against an *existing* assignment is a separate,
  // immediate API call (PATCH .../payment) rather than part of the form's
  // Save — so it gets its own state instead of reusing paymentForm, which
  // is bundled into the create-assignment payload.
  const [editPaymentForm, setEditPaymentForm] = useState({ amount: 0, method: 'transfer', reference: '' });
  const [recordingPayment, setRecordingPayment] = useState(false);
  const [paymentExpanded, setPaymentExpanded] = useState(true);
  const [packageItemsExpanded, setPackageItemsExpanded] = useState(true);

  const openAssignForm = () => {
    setShowAssignForm(true);
    navigate('/asoebi/orders/new');
    window.dispatchEvent(new CustomEvent('assign:form-open', { detail: { isEdit: false } }));
  };

  const resetForm = () => {
    setSelectedGuest(null);
    setAssignType('fabric');
    setNewAssignment({ fabric_id: '', yards: 1, price_per_yard: 0, notes: '', phone: '', email: '' });
    setPayNow(false);
    setPaymentForm({ amount: 0, method: 'transfer', reference: '' });
    setEditingAssignment(null);
    setEditPaymentForm({ amount: 0, method: 'transfer', reference: '' });
  };

  const handleRecordAssignmentPayment = async () => {
    if (!editingAssignment || editPaymentForm.amount <= 0) return;
    setRecordingPayment(true);
    try {
      await recordPayment(editingAssignment.id, {
        payment_amount: String(editPaymentForm.amount),
        payment_method: editPaymentForm.method,
        payment_reference: editPaymentForm.reference || undefined,
      });
      showSuccess('Payment recorded.');
      setEditPaymentForm({ amount: 0, method: 'transfer', reference: '' });
      loadAssignmentPayments(editingAssignment.id).then(setAssignmentPayments).catch(() => {});
      onUpdate();
    } finally {
      setRecordingPayment(false);
    }
  };

  const openEditForm = (assignment: FabricAssignment) => {
    setEditingAssignment(assignment);
    const guest = guests.find(g => sameId('GS', g.id, assignment.guest_id));
    setSelectedGuest(guest ?? null);
    // fabric_kind is what the record itself says the id means, so the form
    // re-opens on the option that was actually chosen. Guessing by looking
    // the id up in `bundles` is what this column exists to replace: bundle
    // ids and fabric-product ids share a numeric space (bundle 7 is "Bride's
    // Crew - Women"; product 7 is an unrelated gadget), so a lookup miss or
    // hit says nothing reliable. The lookup survives only as a fallback for
    // a row written before the column existed.
    setAssignType(isBundleAssignment(assignment) ? 'bundle' : 'fabric');
    setNewAssignment({
      fabric_id: String(assignment.fabric_id ?? ''),
      yards: Number(assignment.yards) || 0,
      price_per_yard: Number(assignment.price_per_yard) || 0,
      notes: assignment.notes ?? '',
      phone: assignment.guest_phone ?? '',
      email: '',
    });
    setShowAssignForm(true);
    // Only actually navigate when this is a real row-click, not the
    // deep-link effect below re-calling this to fill the form after
    // already landing on this exact URL — re-navigating there would reset
    // location.state (and with it, whichever list Cancel/Save should
    // return to) even when nothing about the URL is changing.
    const target = entityPath('fabricAssignment', assignment.id) ?? '/asoebi/orders';
    if (location.pathname !== target) {
      navigate(target, { state: { returnTo: 'assignments' } });
    }
    // Same event the create path fires — and the same shape BundlesView and
    // AsoebiDeliveryDashboard already send from their edit paths. Without it
    // the page stayed in list mode behind the form: the header kept its status
    // filter and "Assign to Guest" button instead of Cancel/Save, and the
    // tracker's metric cards stayed on screen above a single record.
    window.dispatchEvent(new CustomEvent('assign:form-open', {
      detail: { isEdit: true, name: guest?.name ?? null, id: assignment.id },
    }));
  };

  // Landing straight on /asoebi/orders/:id (deep link, refresh, Back) has to
  // fill the form from the record — openEditForm above only runs on a row
  // click. Guarded on showAssignForm so it doesn't refight a click that's
  // already mid-navigation to the same URL.
  useEffect(() => {
    if (!trackerRoute.editId || showAssignForm) return;
    const a = assignments.find(x => String(x.id) === trackerRoute.editId);
    if (!a) return;
    openEditForm(a);
  }, [trackerRoute.editId, assignments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same for /asoebi/orders/new. The create form used to be reachable only by
  // clicking the toolbar button (openAssignForm sets showAssignForm itself),
  // so landing on that URL directly — a bookmark, a reload, or the Overview
  // tab's "Assign Fabric" action, which now navigates here — rendered the
  // list behind an open-looking breadcrumb instead of the form.
  useEffect(() => {
    if (!trackerRoute.open || trackerRoute.editId || showAssignForm) return;
    setShowAssignForm(true);
    window.dispatchEvent(new CustomEvent('assign:form-open', { detail: { isEdit: false } }));
  }, [trackerRoute.open, trackerRoute.editId, showAssignForm]);

  useEffect(() => {
    refreshGuestsFromApi().then(setGuests).catch(() => {});
    refreshBundlesFromApi().then(setBundles).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Linked payments — the same PY-### ledger rows OrderPaymentsPanel shows
  // for a procurement order, surfaced here instead of leaving the sidebar
  // with only receipt attachments and no path to the payment record itself.
  const [assignmentPayments, setAssignmentPayments] = useState<AssignmentPayment[]>([]);
  useEffect(() => {
    if (!editingAssignment) { setAssignmentPayments([]); return; }
    loadAssignmentPayments(editingAssignment.id).then(setAssignmentPayments).catch(() => setAssignmentPayments([]));
  }, [editingAssignment?.id]);

  // The fulfillment records (asoebi_assignments, AZ-xxx) pointing at THIS
  // order. Fetched rather than derived: they live in their own table with
  // their own ids, and nothing this component already holds knows about them.
  const [orderAssignments, setOrderAssignments] = useState<AsoebiAssignment[]>([]);
  useEffect(() => {
    // Packing assignments have no backend route yet (see asoebiAssignmentStatus.ts's
    // former home); loadAsoebiAssignments() always resolves empty until that lands.
    if (!editingAssignment) { setOrderAssignments([]); return; }
    loadAsoebiAssignments().then(setOrderAssignments).catch(() => setOrderAssignments([]));
  }, [editingAssignment?.id]);

  /** Opens the real PY-### record behind one of the Payment section's line
   *  items — the same navigation (and prefilled edit state) the sidebar's
   *  payments list carried before those rows moved into the form column. */
  const openPaymentRecord = (paymentId: string) => {
    const p = assignmentPayments.find(x => String(x.id) === String(paymentId));
    if (!p) return;
    navigate(`/payments/record/${fmtId('PY', p.id)}`, {
      state: {
        editPayment: {
          id: p.id,
          amount: p.amount,
          date: p.date ?? '',
          method: p.method ?? 'bank-transfer',
          status: p.status ?? 'completed',
          reference: p.reference ?? undefined,
          entryType: 'payment',
        },
      },
    });
  };

  // Delivery scheduling/attach/detach (linking an order to a delivery) is deferred: the
  // backend's asoebi/deliveries/:id/lines endpoint needs an order_line id the
  // order read model does not expose yet. See the build plan's 'missing' list.

  useEffect(() => {
    if (triggerAssignForm) {
      openAssignForm();
      onAssignFormOpened?.();
    }
  }, [triggerAssignForm, onAssignFormOpened]); // eslint-disable-line react-hooks/exhaustive-deps

  // Workflow used to live in the breadcrumb (a numbered-circle status
  // stepper the old Header.tsx rendered, driven by these two events) — that
  // stepper was never restored on port/kal (Header.tsx has no asoebi wiring
  // at all today), so only the 'ordered' dispatch inside this file (on
  // create) currently fires 'assign:form-status-change'. The listener below
  // stays live rather than removed: its confirm/apply/revert logic is
  // correct and becomes reachable the moment a breadcrumb stepper is added
  // back, without this file changing again.
  useEffect(() => {
    if (!editingAssignment) return;
    window.dispatchEvent(new CustomEvent('assign:form-status-loaded', { detail: editingAssignment.status }));
  }, [editingAssignment?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!editingAssignment) return;
    const onStatusChange = (e: Event) => {
      const status = (e as CustomEvent<string>).detail as FabricAssignment['status'];
      const previousStatus = editingAssignment.status;
      const revert = () => window.dispatchEvent(new CustomEvent('assign:form-status-loaded', { detail: previousStatus }));
      const apply = async () => {
        // Optimistic, but only after the breadcrumb already moved — a failed
        // PUT must roll the breadcrumb back and say so, not leave it showing
        // a status that was never actually saved server-side.
        setEditingAssignment(prev => (prev ? { ...prev, status } : prev));
        try {
          const updated = await updateAssignment(editingAssignment.id, { status });
          setEditingAssignment(prev => (prev ? { ...prev, ...updated } : prev));
          onUpdate();
        } catch (err) {
          setEditingAssignment(prev => (prev ? { ...prev, status: previousStatus } : prev));
          revert();
          showError(err instanceof Error ? err.message : 'Failed to update the status.');
        }
      };
      // 'paid' is a read-only, payment-axis-derived status (deriveAsoebiStatus
      // server-side) — the server 400s a direct write of it, so there is no
      // UI affordance that dispatches it here; only 'delivered'/'collected'/
      // 'cancelled'/'returned' ever reach this handler.
      // Fulfilment (marking delivered) before payment is unusual, not
      // impossible — a trusted guest or an agreed cash-on-delivery still
      // needs a way through, so this warns rather than blocks outright.
      // Cancelling has to hand the breadcrumb its own status back, since it
      // already moved to "Delivered" optimistically on click.
      if (status === 'delivered' && editingAssignment.payment_status !== 'paid') {
        confirm({
          title: 'Mark as delivered without full payment?',
          description: `${getGuestName(editingAssignment.guest_id)} hasn't paid in full yet. Delivering before payment is unusual — continue anyway?`,
          confirmLabel: 'Deliver anyway',
          variant: 'danger',
        }).then(ok => {
          if (ok) void apply();
          else revert();
        });
        return;
      }
      void apply();
    };
    window.addEventListener('assign:form-status-change', onStatusChange);
    return () => window.removeEventListener('assign:form-status-change', onStatusChange);
  }, [editingAssignment?.id, editingAssignment?.payment_status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssignFabric = async () => {
    // The two paths need different things, and requiring the create path's
    // fields to save an EDIT locked a record out of the app: the update
    // payload below writes neither guest_id nor fabric_id, yet an assignment
    // whose guest cannot be resolved could not be saved at all — Save simply
    // did nothing, with no message. fabric_orders 43 is exactly that row: its
    // guest (393, Kassidy Nwankwo) has guests.wedding_id NULL, so the
    // wedding-scoped /guests endpoint never returns it and no option in the
    // select can match. Its fabric_id is NULL too, so both selects sit on
    // their placeholder.
    const assignment = editingAssignment;
    const now = new Date().toISOString();
    const byName = authUser?.name ?? 'Oceania';

    const total_price = newAssignment.yards * newAssignment.price_per_yard;

    // Written into both the create and update payloads below, so a zero
    // quantity would silently zero out total_price on either path — the
    // label already marks this field required, but nothing enforced it.
    if (!newAssignment.yards || newAssignment.yards <= 0) {
      showError(`Enter ${assignType === 'bundle' ? 'a quantity' : 'a yardage'} greater than zero before saving this assignment.`);
      return;
    }

    if (assignment) {
      // Edit path
      try {
        await updateAssignment(assignment.id, {
          fabric_id: newAssignment.fabric_id || undefined,
          yards: String(newAssignment.yards),
          price_per_yard: String(newAssignment.price_per_yard),
          notes: newAssignment.notes || undefined,
        });
      } catch (err) {
        // Keep the modal open on failure — closing it read as "saved".
        showError(err instanceof Error ? err.message : 'Failed to save assignment.');
        return;
      }
      onUpdate();
      navigate(returnTarget);
      setShowAssignForm(false);
      resetForm();
      window.dispatchEvent(new CustomEvent('assign:form-close'));
      return;
    }

    // Create path — validated here, after the edit path has returned, so the
    // checks apply only where the fields are actually written (and so
    // `selectedGuest` narrows for the payload below).
    if (!selectedGuest) {
      showError('Choose a guest before saving this assignment.');
      return;
    }
    if (!newAssignment.fabric_id) {
      showError('Choose a fabric or package before saving this assignment.');
      return;
    }

    try {
      // The server derives status ('ordered' by default) and total_price
      // (yards * price_per_yard) itself — see createOrder in
      // db/repositories/planning/asoebi/orders.ts; neither is accepted on
      // create. fabric_kind/decision_log/phone/email have no write field on
      // this contract yet — the guest's own phone/e-mail comes from the
      // guests table via guest_id, and notes absorbs anything else worth keeping.
      const created = await createAssignment({
        guest_id: selectedGuest.id,
        fabric_id: newAssignment.fabric_id || undefined,
        yards: String(newAssignment.yards),
        price_per_yard: String(newAssignment.price_per_yard),
        notes: newAssignment.notes.trim() || undefined,
      });
      // Payment (if paid now) is a separate call: the create contract has no payment fields.
      if (payNow && paymentForm.amount > 0) {
        await recordPayment(created.id, {
          payment_amount: String(paymentForm.amount),
          payment_method: paymentForm.method,
          payment_reference: paymentForm.reference || undefined,
          payment_date: new Date().toISOString().slice(0, 10),
        });
      }
    } catch (err) {
      // Same reason as the edit path: a rejected POST used to reject the
      // event handler's floating promise, so the form closed on nothing.
      showError(err instanceof Error ? err.message : 'Failed to create assignment.');
      return;
    }

    onUpdate();
    showSuccess('Asoebi assigned successfully', { persistent: true });
    navigate(returnTarget);
    setShowAssignForm(false);
    resetForm();
    window.dispatchEvent(new CustomEvent('assign:form-close'));
  };

  const handleAssignCancel = () => {
    navigate(returnTarget);
    setShowAssignForm(false);
    resetForm();
    window.dispatchEvent(new CustomEvent('assign:form-close'));
  };

  // Hard-deletes a still-pending portal submission — the same capability
  // PortalOrdersTriage's inline ✗ used to expose from the list row, now
  // reachable from the real assignment edit page instead (Header.tsx only
  // shows the button while status is still 'pending_confirmation').
  const handleAssignReject = async () => {
    const assignment = editingAssignment;
    if (!assignment) return;
    const ok = await confirm({
      title: 'Reject this order?',
      description: `${getGuestName(assignment.guest_id)}'s request will be deleted.`,
      confirmLabel: 'Reject',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      // No reject/convert API for portal-submission orders on port/kal yet —
      // archiving is the closest available action (removes it from the active list).
      await archiveFabricAssignment(assignment.id);
      showSuccess('Order rejected.');
      onUpdate();
      navigate(returnTarget);
      setShowAssignForm(false);
      resetForm();
      window.dispatchEvent(new CustomEvent('assign:form-close'));
    } catch {
      showError('Failed to reject this order.');
    }
  };

  // Stale-closure-safe event listeners
  const saveFnRef = useRef(handleAssignFabric);
  const cancelFnRef = useRef(handleAssignCancel);
  const rejectFnRef = useRef(handleAssignReject);
  saveFnRef.current = handleAssignFabric;
  cancelFnRef.current = handleAssignCancel;
  rejectFnRef.current = handleAssignReject;

  useEffect(() => {
    const onSave = () => saveFnRef.current();
    const onCancel = () => cancelFnRef.current();
    const onOpen = () => openAssignForm();
    const onReject = () => void rejectFnRef.current();
    window.addEventListener('assign:save', onSave);
    window.addEventListener('assign:cancel', onCancel);
    window.addEventListener('assign:open', onOpen);
    window.addEventListener('assign:reject', onReject);
    return () => {
      window.removeEventListener('assign:save', onSave);
      window.removeEventListener('assign:cancel', onCancel);
      window.removeEventListener('assign:open', onOpen);
      window.removeEventListener('assign:reject', onReject);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the raw reference when the guest can't be resolved. A bare "Unknown
  // Guest" gives no way to tell which record is broken (or to search for it);
  // the edit form already names the id in its hint, so the row should too.
  const getGuestName = (guestId: string | null) =>
    guests.find(g => sameId('GS', g.id, guestId))?.name
    ?? (guestId ? `Unknown Guest (${guestId})` : 'Unknown Guest');
  // guests.rsvp_status stores lowercase values ('confirmed') — fine for
  // logic, but reads as unstyled next to the sidebar's other Title Case
  // labels. (guests.side goes through guestSideLabel instead: capitalising it
  // rendered 'Alfa-grace', and it isn't a bride/groom binary at all.)
  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  /**
   * Which table this record's fabric_id names, and what to call it. Both
   * defer to `fabricRef`, which is where the fabric_kind-over-id-guess rule
   * lives for every call site — see that module for why the id alone can't
   * decide (bundle 7 is the ₦180,000 "Bride's Crew - Women" package, product
   * 7 is an unrelated gadget).
   */
  const isBundleAssignment = (a: FabricRef) => isBundleRef(a, bundles);

  const rawFilteredAssignments = assignments.filter(
    a => statusFilter === 'all' || a.status === statusFilter
  );

  // Sorted, not sortable: there are no column headers to click here any more
  // (this component is a detail page), but the ordering still has to match
  // the list the Prev/Next buttons below are walking.
  const { sortedData: filteredAssignments } = useSortableTable(rawFilteredAssignments);

  // Reports the current (filtered, sorted) id list up to the breadcrumb
  // header — same event-based pattern quote:list-ready/procurement:list-ready
  // already use — so Header.tsx's Prev/Next buttons walk what this list
  // actually shows rather than a re-derived ordering.
  useEffect(() => {
    const ids = filteredAssignments.map(a => a.id);
    window.dispatchEvent(new CustomEvent('assignment:list-ready', { detail: ids }));
  }, [filteredAssignments]);

  const activeFabrics = fabrics.filter(f => f.is_active !== false);

  // ── Assign form ────────────────────────────────────────────────────────────
  if (showAssignForm) {
    const selectedBundle = assignType === 'bundle' ? bundles.find(b => b.id === newAssignment.fabric_id) : null;
    const selectedFabric = assignType === 'fabric' ? activeFabrics.find(f => f.id === newAssignment.fabric_id) : null;
    const unitPrice = newAssignment.price_per_yard;
    const totalPreview = newAssignment.fabric_id && newAssignment.yards > 0 && unitPrice > 0
      ? newAssignment.yards * unitPrice
      : null;

    // Merged fabric + bundle options: value prefixed f: or b: to distinguish
    const fabricBundleOptions = [
      ...activeFabrics.map(f => ({
        value: `f:${f.id}`,
        label: f.color ? `${f.name} — ${f.color}` : f.name,
      })),
      ...bundles.filter(b => !b.types || b.types.length === 0 || b.types.includes('asoebi')).map(b => ({
        value: `b:${b.id}`,
        label: `[Package] ${b.name} — ${formatMoney(b.price)}`,
      })),
    ];

    const selectedFabricBundleValue = newAssignment.fabric_id
      ? `${assignType === 'bundle' ? 'b' : 'f'}:${newAssignment.fabric_id}`
      : '';

    const handleFabricBundleChange = (prefixedId: string) => {
      if (prefixedId.startsWith('b:')) {
        const id = prefixedId.slice(2);
        const b = bundles.find(x => x.id === id);
        setAssignType('bundle');
        setNewAssignment(prev => ({ ...prev, fabric_id: id, price_per_yard: b?.price || 0, yards: 1 }));
      } else {
        const id = prefixedId.slice(2);
        const f = activeFabrics.find(x => x.id === id);
        setAssignType('fabric');
        setNewAssignment(prev => ({ ...prev, fabric_id: id, price_per_yard: Number(f?.price) || 0 }));
      }
    };

    // Once staff have confirmed a raw guest submission is real (Ordered),
    // or it's gone further (Paid, Delivered), it reads as a settled record
    // — Guest/Fabric/Quantity staying editable after that let a confirmed
    // order quietly become a different guest's order with no trace. The
    // status stepper in the breadcrumb (Header.tsx's ASSIGNMENT_STEPS) is
    // still how to move it back to Awaiting if a correction is genuinely
    // needed.
    const isLocked = editingAssignment
      ? ['ordered', 'paid', 'delivered'].includes(editingAssignment.status ?? '')
      : false;

    // A guest can have more than one asoebi_orders row — an earlier
    // reconciled order plus a later portal resubmission, say — each with
    // its own independent payment status. Nothing else on this page hints
    // that a second order for the same guest exists, which is exactly what
    // confused staff into asking whether an unpaid order had actually been
    // paid (they were looking at the wrong one). Surfaced from the same
    // `assignments` list already loaded for the tracker table, not a
    // separate fetch.
    const otherGuestOrders = editingAssignment
      ? assignments.filter(a => a.id !== editingAssignment.id && String(a.guest_id) === String(editingAssignment.guest_id))
      : [];

    // Scheduling or attaching a delivery is the same "fulfilling before
    // payment" case the status stepper warns about — same soft block, same
    // override, just reached from a different button.
    const confirmFulfillmentIfUnpaid = async (): Promise<boolean> => {
      if (!editingAssignment || editingAssignment.payment_status === 'paid') return true;
      return confirm({
        title: 'Schedule a delivery without full payment?',
        description: `${getGuestName(editingAssignment.guest_id)} hasn't paid in full yet. Scheduling delivery before payment is unusual — continue anyway?`,
        confirmLabel: 'Schedule anyway',
        variant: 'danger',
      });
    };

    return (
      <>
      <div className="detail-layout">
      <div className="inline-form-page gat-assign-form-page">
          {isLocked && (
            <p className="entity-state-banner entity-state-banner--locked gat-mt-0">
              Locked — status is {editingAssignment?.status === 'delivered' ? 'Delivered' : editingAssignment?.status === 'paid' ? 'Paid' : 'Ordered'}. Move it back to Awaiting on the status bar above to correct a mistake.
            </p>
          )}
          {/* Row 1: Guest | Fabric or Bundle */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Guest <span className="form-required">*</span></label>
              <SearchableSelect
                value={selectedGuest?.id || ''}
                onChange={id => setSelectedGuest(guests.find(g => g.id === id) || null)}
                options={guests.map(g => ({ value: g.id, label: g.name }))}
                placeholder="Choose a guest..."
                disabled={isLocked}
              />
              {editingAssignment && !selectedGuest && (
                <p className="form-hint">
                  This assignment's guest ({editingAssignment.guest_id || 'not set'}) isn't in this
                  wedding's guest list, so it can't be preselected. Saving keeps the record's
                  existing guest link — the other fields still save normally.
                </p>
              )}
            </div>
            {!selectedGuest && (
              <div className="form-group">
                <label className="form-label">Phone / Email</label>
                <p className="form-hint">
                  No guest is linked yet — record their contact here instead of in Notes, so it's
                  searchable and matches a real guest later.
                </p>
                <div className="gat-contact-fields">
                  <input
                    type="tel"
                    value={newAssignment.phone}
                    onChange={e => setNewAssignment(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="Phone number"
                    disabled={isLocked}
                  />
                  <input
                    type="email"
                    value={newAssignment.email}
                    onChange={e => setNewAssignment(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Email address"
                    disabled={isLocked}
                  />
                </div>
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Fabric or Package <span className="form-required">*</span></label>
              <SearchableSelect
                value={selectedFabricBundleValue}
                onChange={handleFabricBundleChange}
                options={fabricBundleOptions}
                placeholder="Choose a fabric or package..."
                disabled={isLocked}
              />
            </div>
          </div>

          {/* Row 2: Quantity | Price */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{assignType === 'bundle' ? 'Quantity (packages) *' : 'Yards *'}</label>
              <input
                type="number"
                min={assignType === 'bundle' ? '1' : '0.5'}
                step={assignType === 'bundle' ? '1' : '0.5'}
                value={newAssignment.yards}
                onChange={e => setNewAssignment(prev => ({ ...prev, yards: parseFloat(e.target.value) || 0 }))}
                disabled={isLocked}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{assignType === 'bundle' ? 'Bundle Price' : `Price per Yard${currencySymbolDisplay ? ` (${currencySymbolDisplay})` : ` (${currencyUnit})`}`}</label>
              <div className="gat-readonly-field">
                {unitPrice > 0 ? formatMoney(unitPrice) : '—'}
              </div>
            </div>
          </div>

          {/* Notes */}
          <RichTextEditor
            label="Notes"
            value={newAssignment.notes}
            onChange={val => setNewAssignment(prev => ({ ...prev, notes: val }))}
            placeholder="Special requests or notes..."
            rows={3}
          />

          {/* What's actually inside the selected package. This was a bare
              <ul> of "5× Teal Beaded Lace" in the sidebar, which put the
              order's real contents in the summary column while the form
              column showed only a package name. Rendered with the shared
              LineItemsEditor — the same component Quote Details and
              ProcurementOrderEdit use — so a package's contents read like
              every other set of line items in the app.

              Read-only: these rows belong to the BUNDLE (bundles.items), not
              to this order. Nothing in the schema carries per-order item
              overrides, so an editable grid here would be inventing a data
              model; edit the package itself on the Bundles tab.

              Collapsible header matches the Delivery/Payment sections below
              rather than LineItemsEditor's own `section` chrome, which draws
              a fixed heading that can't fold away. */}
          {selectedBundle && selectedBundle.items.length > 0 && (<>
            <div className="poe-collapsible-row">
              <button
                type="button"
                className="poe-sub-heading poe-collapsible-heading"
                onClick={() => setPackageItemsExpanded(v => !v)}
                aria-expanded={packageItemsExpanded}
              >
                Package Items
                <Icon name={packageItemsExpanded ? 'chevronDown' : 'chevronRight'} size={14} />
              </button>
              <span className="poe-section-count">
                {selectedBundle.items.length} {selectedBundle.items.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            {packageItemsExpanded && (
              <LineItemsEditor
                items={selectedBundle.items.map((item, i) => ({
                  id: `${selectedBundle.id}-${i}`,
                  values: {
                    productName: item.productName,
                    yards: String(item.quantity),
                    unitLabel: item.unitLabel ?? '',
                  },
                }))}
                fields={PACKAGE_ITEM_FIELDS}
                onChange={() => { /* read-only — see the note above */ }}
                disabled
                disabledMessage={`These come from the ${selectedBundle.name} package — edit them on the Bundles tab.`}
                showAddButton={false}
              />
            )}
          </>)}

          {/* Total preview shown in the sidebar's Order Total card instead —
              this block duplicated it at the bottom of the form. */}

          {/* Optional: record payment now — only shown when creating */}
          {!editingAssignment && (
          <>
          <div className="form-section-divider" />
          <label className="gat-form-checkbox-row">
            <input type="checkbox" checked={payNow} onChange={e => setPayNow(e.target.checked)} />
            <span>Record payment now</span>
          </label>
          {payNow && (
            <div className="form-payment-section">
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Amount Paid{currencySymbolDisplay ? ` (${currencySymbolDisplay})` : ` (${currencyUnit})`}</label>
                  <CurrencyInput
                    min={0}
                    placeholder="0"
                    value={paymentForm.amount || ''}
                    onChange={e => setPaymentForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <SearchableSelect
                    value={paymentForm.method}
                    onChange={value => setPaymentForm(prev => ({ ...prev, method: value }))}
                    options={[
                      { value: 'transfer', label: 'Bank transfer' },
                      { value: 'cash', label: 'Cash' },
                      { value: 'pos', label: 'POS' },
                      { value: 'other', label: 'Other' },
                    ]}
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Reference / receipt number</label>
                <input
                  type="text"
                  placeholder="Optional"
                  value={paymentForm.reference}
                  onChange={e => setPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                />
              </div>
            </div>
          )}
          </>
          )}

          {/* Workflow moved to the breadcrumb (see the useEffect pair above
              and Header.tsx's ASSIGNMENT_STEPS) — same treatment quotes,
              deliveries and procurement orders already got. */}
          {editingAssignment && (
          <>
          <div className="form-section-divider" />
          {/* Same poe-collapsible-row/poe-collapsible-heading shape as the
              Delivery section above it — one consistent "section header
              with a toggle, a summary, and its action button" pattern for
              every collapsible block on this page. */}
          <div className="poe-collapsible-row">
            <button
              type="button"
              className="poe-sub-heading poe-collapsible-heading"
              onClick={() => setPaymentExpanded(v => !v)}
              aria-expanded={paymentExpanded}
            >
              Payment
              <Icon name={paymentExpanded ? 'chevronDown' : 'chevronRight'} size={14} />
            </button>
            <span className="poe-section-count">
              {formatMoney(Number(editingAssignment.payment_amount) || 0, editingAssignment.currency)} of {formatMoney(Number(editingAssignment.total_price), editingAssignment.currency)} paid
              {assignmentPayments.length > 0 && ` · ${assignmentPayments.length} ${assignmentPayments.length === 1 ? 'payment' : 'payments'}`}
            </span>
          </div>
          {paymentExpanded && (
            <div className="gat-payment-section">
              {/* The real payments.asoebi_assignment_id rows, as line items.
                  They used to sit in the sidebar as an .opp-list; the money
                  against this order is not summary chrome, it's the section's
                  own content, and it now reads like every other line-item
                  list in the app (quotes, procurement orders).

                  Read-only: each row is a real PY-### record with its own
                  edit page, and nothing here saves a payment in place — the
                  arrow opens that record, which is where it's edited. The
                  "record a new payment" inputs stay BELOW the list rather
                  than above it: they're an append action, and reading what
                  has already been paid before adding to it is the order the
                  task itself happens in. */}
              {assignmentPayments.length > 0 && (
                <LineItemsEditor
                  items={assignmentPayments.map(p => ({
                    id: String(p.id),
                    values: {
                      payment: recordLabel('payment', p.id),
                      date: p.date || '',
                      method: p.method ?? '',
                      reference: p.reference ?? '',
                      amount: String(p.amount ?? 0),
                    },
                  }))}
                  fields={PAYMENT_ITEM_FIELDS}
                  onChange={() => { /* read-only — edit the PY- record itself */ }}
                  disabled
                  showAddButton={false}
                  rowAction={{
                    icon: 'arrowRight',
                    title: item => `Open ${item.values.payment}`,
                    onClick: item => openPaymentRecord(item.id),
                  }}
                />
              )}
              {/* Recording a payment is ONE action on three short fields, so
                  it reads as one row ending in its own button — not three
                  stacked blocks and a button orphaned below them. */}
              <div className="form-row gat-payment-record-row">
                <div className="form-group">
                  <label className="form-label">Amount to record{currencySymbolDisplay ? ` (${currencySymbolDisplay})` : ` (${currencyUnit})`}</label>
                  <CurrencyInput
                    min={0}
                    placeholder="0"
                    value={editPaymentForm.amount || ''}
                    onChange={e => setEditPaymentForm(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <SearchableSelect
                    value={editPaymentForm.method}
                    onChange={value => setEditPaymentForm(prev => ({ ...prev, method: value }))}
                    options={[
                      { value: 'transfer', label: 'Bank transfer' },
                      { value: 'cash', label: 'Cash' },
                      { value: 'pos', label: 'POS' },
                      { value: 'other', label: 'Other' },
                    ]}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Reference / receipt number</label>
                  <input
                    type="text"
                    placeholder="Optional"
                    value={editPaymentForm.reference}
                    onChange={e => setEditPaymentForm(prev => ({ ...prev, reference: e.target.value }))}
                  />
                </div>
                <div className="gat-payment-record-action">
                  <button
                    type="button"
                    className="primary-btn"
                    disabled={editPaymentForm.amount <= 0 || recordingPayment}
                    onClick={handleRecordAssignmentPayment}
                  >
                    {recordingPayment ? 'Recording…' : 'Record Payment'}
                  </button>
                </div>
              </div>
            </div>
          )}
          </>
          )}
      </div>

      <aside className="detail-sidebar">
        {/* Accept/Reject — only reachable while still 'pending_confirmation'.
            Moved here from the top toolbar so a still-unreviewed
            submission's two real outcomes (become a real order, or get
            deleted outright) sit together, separate from Save/Cancel
            above (which just persist whatever's currently in the form). */}
        {editingAssignment?.status === 'pending_confirmation' && (
          <section className="side-section">
            <h4>Review Submission</h4>
            <div className="decision-actions">
              <button
                type="button"
                className="decision-btn decision-btn--accept"
                onClick={() => {
                  window.dispatchEvent(new CustomEvent('assign:form-status-change', { detail: 'ordered' }));
                  window.dispatchEvent(new CustomEvent('assign:save'));
                }}
              >
                <Icon name="check" size={16} /> Accept
              </button>
              <button
                type="button"
                className="decision-btn decision-btn--reject"
                onClick={handleAssignReject}
              >
                <Icon name="x" size={16} /> Reject
              </button>
            </div>
          </section>
        )}
        <section className="side-section">
          <h4>Guest</h4>
          {selectedGuest ? (
            <>
              <div className="stat-row">
                <span>Name</span>
                <button type="button" className="stat-row-link" onClick={() => navigate(entityPath('guest', selectedGuest.id) ?? '/guests')}>
                  {selectedGuest.name}
                </button>
              </div>
              {selectedGuest.phone && <div className="stat-row"><span>Phone</span><strong>{selectedGuest.phone}</strong></div>}
              {selectedGuest.side && <div className="stat-row"><span>Side</span><strong>{sideLabel(selectedGuest.side)}</strong></div>}
              <div className="stat-row"><span>RSVP</span><strong>{capitalize(selectedGuest.rsvp_status)}</strong></div>
            </>
          ) : (
            <p className="rpf-sidebar-empty">Select a guest to see their details.</p>
          )}
        </section>

        {/* Only rendered when the guest actually has another order — this is
            the one place staff would otherwise have no way to know a second,
            separately-tracked order for this same guest exists elsewhere. */}
        {otherGuestOrders.length > 0 && (
          <section className="side-section opp-section">
            <div className="opp-header"><h4>Other Orders for this Guest</h4></div>
            <ul className="opp-list">
              {otherGuestOrders.map(o => (
                <li
                  key={o.id}
                  className="opp-row opp-row-link"
                  role="link"
                  tabIndex={0}
                  onClick={() => navigate(entityPath('fabricAssignment', o.id) ?? '/asoebi/orders')}
                  onKeyDown={e => { if (e.key === 'Enter') (e.currentTarget as HTMLElement).click(); }}
                >
                  <span className="opp-amount">
                    {formatMoney(Number(o.payment_amount) || 0, o.currency)} of {formatMoney(Number(o.total_price), o.currency)}
                  </span>
                  <span className="opp-meta">
                    {/* These are ORDERS, so they carry the order prefix. They
                        used to render as AZ- ids linking to
                        /asoebi/assignments/AZ-<order id> — the same row under
                        a second identity, from before assignments became
                        real records with their own ids. */}
                    <span className="opp-id"><RecordLabelText kind="fabricAssignment" id={o.id} /></span>
                    {' · '}{o.payment_status === 'paid' ? 'Paid' : o.payment_status === 'partial' ? 'Partial' : 'Unpaid'}
                  </span>
                  <span className={`fulfillment-badge fulfillment-badge--${o.status === 'delivered' ? 'complete' : 'draft'}`}>
                    {o.status === 'delivered' ? 'Complete' : 'Draft'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="side-section">
          <h4>{assignType === 'bundle' ? 'Package' : 'Fabric'}</h4>
          {selectedBundle ? (
            /* The item breakdown that used to sit here moved into the form
               column as line items (see Package Items above) — the sidebar is
               for this record's own at-a-glance figures, not for a table. */
            <div className="stat-row"><span>Price</span><strong>{formatMoney(selectedBundle.price)}</strong></div>
          ) : selectedFabric ? (
            <>
              <div className="stat-row"><span>Price / yard</span><strong>{formatMoney(Number(selectedFabric.price), selectedFabric.currency)}</strong></div>
              <div className="stat-row"><span>Stock remaining</span><strong>{selectedFabric.stock}</strong></div>
            </>
          ) : (
            <p className="rpf-sidebar-empty">Select a fabric or package to see details.</p>
          )}
        </section>

        {totalPreview !== null && (
          <section className="side-section">
            <div className="stat-row"><span>Order Total</span><strong>{formatMoney(totalPreview)}</strong></div>
          </section>
        )}
        {/* The real asoebi_assignments rows fulfilling this order. This
            section used to be a single "Linked Assignment" row pointing at
            /asoebi/assignments/AZ-<this order's id> — the same record wearing
            a second prefix. An assignment is now its own row in its own
            table, one order can have several, and their ids are unrelated to
            the order's, so the link has to come from the data. */}
        {editingAssignment && (
          <section className="side-section opp-section">
            <div className="opp-header"><h4>Assignments</h4></div>
            {orderAssignments.length === 0 ? (
              <p className="rpf-sidebar-empty">
                Nothing fulfilling this order yet — add one on the Assignments tab.
              </p>
            ) : (
              <div className="ds-linked-rows">
                {orderAssignments.map(az => (
                  <LinkedRecordRow
                    key={az.id}
                    href={undefined}
                    id={recordLabel('fabricAssignment', az.id)}
                    label="Assignment"
                    meta="Draft"
                  />
                ))}
              </div>
            )}
          </section>
        )}
        {/* The recorded payments list that used to sit here moved into the
            form column as line items (see the Payment section above) — the
            money against this order is the section's own content, not
            sidebar summary chrome. */}
        {/* A transfer receipt evidences a specific payment, not the
            assignment as a whole — it belongs on the PY-### record itself
            (RecordPaymentFormPage already has its own attachments UI at
            entity_type='payment'), reachable from the Payment section's
            line items in the form column.
            This panel is for anything else worth attaching to the
            assignment record directly (a confirmation screenshot, a size
            chart, whatever doesn't belong to one specific payment). */}
        {editingAssignment && (
          <AttachmentsPanel
            entityType="fabric_order"
            entityId={String(editingAssignment.id)}
            title="Attachments"
            uploadLabel="+ Attach a file"
            disabledMessage="Save this assignment first to attach a file."
            onError={showError}
          />
        )}
      </aside>
      </div>

      {/* An order carries money and a real lifecycle (Awaiting → Ordered →
          Paid → Delivered) and had no thread and no trail — every "why is
          this still awaiting" question had to be answered from memory. */}
      <RecordActivity
        entityType="asoebi_order"
        entityId={editingAssignment ? String(editingAssignment.id) : undefined}
        historyType="asoebi_order"
        statusLabels={ORDER_STATUS_LABELS}
      />
      </>
    );
  }

  // ── No record ──────────────────────────────────────────────────────────────
  // This component is mounted only on an order DETAIL url (AsoebiManager
  // gates it on isAsoebiOrderDetailUrl; the list is AsoebiOrdersList). So the
  // form above is the only real view, and falling through to here means the
  // orders have not arrived yet (the deep-link effect can only fill the form
  // once the record is in `assignments`), or the id in the url matches no
  // order.
  return (
    <div className="guest-asoebi-tracker">
      {assignments.length === 0 ? (
        <EmptyState icon="list" title="Loading order…" compact />
      ) : (
        <EmptyState
          icon="alert"
          title="Order not found"
          description={`No asoebi order matches ${trackerRoute.editId ? recordLabel('fabricAssignment', trackerRoute.editId) : 'this address'}. It may have been rejected or deleted — open it from the Orders list instead.`}
        />
      )}
    </div>
  );
};

export default GuestAsoebiTracker;
